import { useSignMessage } from "wagmi";
import { useMutation } from "@tanstack/react-query";
import { useCreateAccount } from "./useCreateAccount";
import { useCreateSession } from "./useCreateSession";
import { useEnsureNoSessionAndFetchNonce } from "./useEnsureNoSessionAndFetchNonce";
import { createSignInWithWalletMessage } from "../utils/siww";
import { useGetAccountByAddress } from "./useGetAccountByAddress";
import useWalletAccount from "./useWalletAccount";
function useAuthenticateWithWallet(shouldCreateAccount = false, mutationOptions = {}) {
    const { walletAddress, chainId, connector, networkFamily } = useWalletAccount();
    const { ensureNoSessionAndFetchNonce } = useEnsureNoSessionAndFetchNonce();
    const { createSessionAsync } = useCreateSession();
    const { createAccountAsync } = useCreateAccount();
    const { signMessageAsync } = useSignMessage();
    const { refetch: getAccountByAddress } = useGetAccountByAddress(walletAddress, {
        enabled: false,
        retry: false,
    });
    const { mutate, mutateAsync, ...signInMutationRestParameters } = useMutation({
        mutationFn: async () => {
            if (!walletAddress) {
                throw new Error("Sign in error: Wallet not connected!");
            }
            const nonce = await ensureNoSessionAndFetchNonce();
            const messageResult = createSignInWithWalletMessage(walletAddress, nonce, networkFamily, chainId);
            const signatureResult = await signMessageAsync({
                message: messageResult,
                connector,
            });
            if (shouldCreateAccount) {
                const getAccountByAddressResult = await getAccountByAddress();
                if (getAccountByAddressResult.data) {
                    throw new Error("Wallet authentication failed: Account creation failed. Account already exists.");
                }
                if (getAccountByAddressResult.error) {
                    throw new Error(`Wallet authentication failed: Account creation failed. Could not verify if account already exists: ${getAccountByAddressResult.error}`);
                }
                // Fetch was successful, but no account was found
                try {
                    const createAccountResult = await createAccountAsync({
                        type: "wallet",
                        message: messageResult,
                        signature: signatureResult,
                    });
                    if (!createAccountResult) {
                        throw new Error("Failed to create new account");
                    }
                }
                catch (error) {
                    throw new Error(`Wallet authentication failed: Account creation failed: ${error}`);
                }
            }
            return (await createSessionAsync({
                type: "wallet",
                message: messageResult,
                signature: signatureResult,
            }));
            // ^ By passing type: "wallet" we know for sure the return type is Session
        },
        ...mutationOptions,
    });
    return {
        authenticateWithWallet: mutate,
        authenticateWithWalletAsync: mutateAsync,
        ...signInMutationRestParameters,
    };
}
export { useAuthenticateWithWallet };
//# sourceMappingURL=useAuthenticateWithWallet.js.map